import { useState, useEffect, useRef } from 'react';
import {
  COMPETITION_COMPLETED_STATUS,
  COMPETITION_UPCOMING_STATUS,
} from 'constants/competitions';

const useCompetitionCountdown = (competition, onCountdownEnd, suffix) => {
  const [timeLeft, setTimeLeft] = useState('00:00:00');
  const [retryStatus, setRetryStatus] = useState(null);
  const statusRef = useRef(competition.status);
  const timerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 3000;

  const formatTime = (num) => String(Math.max(0, Math.floor(num))).padStart(2, '0');

  const formatTimeString = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${formatTime(hours)}:${formatTime(minutes)}:${formatTime(seconds)}`;
  };

  const calculateTimeLeft = (targetTime) => {
    if (!targetTime) return 0;

    try {
      let targetDate;

      if (typeof targetTime === 'number') {
        targetDate = new Date(targetTime);
      } else if (targetTime instanceof Date) {
        targetDate = targetTime;
      } else {
        const cleanedTime = targetTime.trim();

        if (cleanedTime.endsWith('Z')) {
          targetDate = new Date(cleanedTime);
        } else if (cleanedTime.includes('.')) {
          targetDate = new Date(`${cleanedTime.split('.')[0]}Z`);
        } else if (cleanedTime.includes('+')) {
          targetDate = new Date(cleanedTime);
        } else {
          targetDate = new Date(`${cleanedTime}Z`);
        }
      }

      if (isNaN(targetDate.getTime())) {
        console.warn('Invalid date format received:', targetTime);
        return 0;
      }

      const nowUTC = Date.now();
      const targetUTC = targetDate.getTime();

      return Math.max(0, Math.ceil((targetUTC - nowUTC) / 1000));
    } catch (error) {
      console.warn('Error calculating time left:', error);
      return 0;
    }
  };

  const clearTimers = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const handleRetry = () => {
    // Only proceed with retry if the competition status hasn't changed
    if (competition.status === statusRef.current && retryCountRef.current < MAX_RETRIES) {
      // Set appropriate retry status message based on current competition status
      if (competition.status === COMPETITION_UPCOMING_STATUS) {
        setRetryStatus('Starting...');
      } else {
        // For any other status (typically ongoing)
        setRetryStatus('Completing...');
      }

      retryCountRef.current += 1;
      onCountdownEnd();

      // Schedule next retry if not reached max retries and status still matches
      if (retryCountRef.current < MAX_RETRIES) {
        retryTimerRef.current = setTimeout(() => {
          // Double-check status match before next retry
          if (competition.status === statusRef.current) {
            handleRetry();
          } else {
            // Status changed, reset retry status
            setRetryStatus(null);
          }
        }, RETRY_DELAY);
      }
    } else {
      // We're done with retries or status changed, reset retry status
      setRetryStatus(null);
    }
  };

  useEffect(() => {
    if (competition.status !== statusRef.current) {
      statusRef.current = competition.status;
      retryCountRef.current = 0;
      setRetryStatus(null);
      clearTimers();
    }

    if (competition.status === COMPETITION_COMPLETED_STATUS) {
      clearTimers();
      setTimeLeft('Completed');
      setRetryStatus(null);
      return clearTimers;
    }

    const targetTime = competition.status === COMPETITION_UPCOMING_STATUS
      ? competition.start
      : competition.end;

    const tick = () => {
      const totalSeconds = calculateTimeLeft(targetTime);

      if (totalSeconds <= 0) {
        clearTimers();

        // Only set timeLeft to '00:00:00' if not in retry mode
        if (!retryStatus) {
          setTimeLeft('00:00:00');
        }

        onCountdownEnd();

        // Reset retry count and start retry process only if status matches
        retryCountRef.current = 0;
        if (competition.status === statusRef.current) {
          if (competition.status === COMPETITION_UPCOMING_STATUS) {
            setRetryStatus('Starting...');
          } else {
            // For any other status (typically ongoing)
            setRetryStatus('Completing...');
          }
          retryTimerRef.current = setTimeout(handleRetry, RETRY_DELAY);
        }
      } else {
        setTimeLeft(`${formatTimeString(totalSeconds)} ${suffix || ''}`);
        setRetryStatus(null); // Clear any retry status when countdown is running
      }
    };

    clearTimers();
    tick();
    timerRef.current = setInterval(tick, 1000);

    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition.status, competition.start, competition.end]);

  // Return retry status if active, otherwise return timeLeft
  return {
    retryStatus, timeLeft,
  };
};

export default useCompetitionCountdown;
